import { useCallback, useEffect, useState } from 'react'
import { InputDate, RColumn, RemoteTable } from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import {
  ContractOperationLogFilter,
  ContractOperationLogItemDTO,
  ContractOperationLogOptionsDTO,
  ContractOperationStatus,
  ContractOperationType,
} from '../../dto/contractOperationLog/ContractOperationLogDTO'
import { getContractOperationLogOptions, getContractOperationLogs } from '../../services/ContractOperationLogController'
import { parseApiError } from '../../services/apiError'
import { toISOString } from '../../utils/localeHelpers'
import useNotify from '../../hooks/useNotify'
import NvTooltips from '../../components/novarium/NvTooltips'
import ContractOperationLogDetailView from './ContractOperationLogDetailView'
import { describeCounts, EMPTY, formatDateTime } from './contractOperationLogFormat'
import './ContractOperationLog.scss'

const LOAD_ERROR = 'No se pudo cargar el historial. Intentá de nuevo.'
const EMPTY_OPTIONS: ContractOperationLogOptionsDTO = { operationTypes: [], statuses: [], healthEntities: [] }

type Row = ContractOperationLogItemDTO
const startedAtBody = (row: Row) => formatDateTime(row.startedAt)
const userBody = (row: Row) => (
  <span className='nv-truncate nv-tip' data-pr-tooltip={row.userName ?? undefined}>{row.userName || EMPTY}</span>
)
const countsBody = (row: Row) => describeCounts(row)
const descriptionBody = (row: Row) => (
  <span className='nv-truncate nv-tip' data-pr-tooltip={row.description ?? undefined}>{row.description || EMPTY}</span>
)
const NO_FILTERS: ContractOperationLogFilter = {}
// Tabla de layout fijo: las columnas en px miden eso, Parámetros un porcentaje y las "auto" se reparten lo que queda
// (si no, el sobrante se reparte también entre las fijas y la fecha queda más ancha que su dato).
const STARTED_AT_STYLE = { width: 160 }
const STATUS_STYLE = { width: 220 }
const COUNTS_STYLE = { width: 200 }
const AUTO_STYLE = { width: 'auto' }
const DESCRIPTION_STYLE = { width: '30%' }

function isoDate(date: Date | null): string | undefined {
  return date ? toISOString(date)!.substring(0, 10) : undefined
}

export default function ContractOperationLogView() {
  const notify = useNotify()
  const [options, setOptions] = useState<ContractOperationLogOptionsDTO>(EMPTY_OPTIONS)
  const [fromDate, setFromDate] = useState<Date | null>(null)
  const [toDate, setToDate] = useState<Date | null>(null)
  const [operationType, setOperationType] = useState<ContractOperationType | null>(null)
  const [status, setStatus] = useState<ContractOperationStatus | null>(null)
  const [healthEntityId, setHealthEntityId] = useState<number | null>(null)
  const [toError, setToError] = useState<string | undefined>()
  /** Filtros de la última consulta: al abrir, todas las operaciones. */
  const [appliedFilter, setAppliedFilter] = useState<ContractOperationLogFilter>(NO_FILTERS)
  const [viewing, setViewing] = useState<Row | null>(null)
  const [backFromDetail, setBackFromDetail] = useState(false)

  useEffect(() => {
    getContractOperationLogOptions()
      .then(setOptions)
      .catch((error: unknown) => {
        parseApiError(error)
        notify.error('No se pudieron cargar las opciones de los filtros. Intentá de nuevo.')
      })
  }, [notify])

  function handleConsult() {
    if (fromDate && toDate && fromDate > toDate) {
      setToError('Ingresá una fecha igual o posterior a la fecha desde')
      return
    }
    setToError(undefined)
    setAppliedFilter({
      from: isoDate(fromDate),
      to: isoDate(toDate),
      operationType: operationType ?? undefined,
      status: status ?? undefined,
      healthEntityId: healthEntityId ?? undefined,
    })
  }

  function clearFilters() {
    setFromDate(null)
    setToDate(null)
    setOperationType(null)
    setStatus(null)
    setHealthEntityId(null)
    setToError(undefined)
    setAppliedFilter(NO_FILTERS)
  }

  const loadPage = useCallback((offset: number, pageSize: number) => (
    getContractOperationLogs(appliedFilter, offset, pageSize).catch((error: unknown) => {
      const apiError = parseApiError(error)
      const rangeError = apiError.details?.find(detail => detail.field === 'to')?.message
      if (rangeError) setToError(rangeError)
      else notify.error(LOAD_ERROR)
      return [] as Row[]
    })
  ), [appliedFilter, notify])

  const rowActions = useCallback((row: Row) => (
    <Button
      type='button'
      text
      className='nv-icon nv-tip'
      icon='pi pi-eye'
      aria-label={`Ver detalle de ${row.operationTypeLabel}`}
      data-pr-tooltip='Ver detalle'
      onClick={() => setViewing(row)}
    />
  ), [])

  if (viewing) {
    return (
      <ContractOperationLogDetailView
        operation={viewing}
        onBack={() => {
          setViewing(null)
          setBackFromDetail(true)
        }}
      />
    )
  }

  return (
    <div className='nv-scope nv-page contract-operation-log'>
      <NvTooltips />
      <header className='nv-page-head'>
        <h1 className='nv-h1'>Historial de operaciones</h1>
      </header>

      <section className='nv-section' aria-label='Filtros'>
        <div className='nv-form-row'>
          <InputDate
            label='Desde'
            className='contract-operation-log__date'
            value={fromDate}
            onChange={(e: { value?: Date | null }) => {
              setFromDate(e.value ?? null)
              setToError(undefined)
            }}
          />
          <InputDate
            label='Hasta'
            className='contract-operation-log__date'
            value={toDate}
            onChange={(e: { value?: Date | null }) => {
              setToDate(e.value ?? null)
              setToError(undefined)
            }}
            error={!!toError}
            caption={toError}
          />
          <div className='nv-field nv-w-m'>
            <label htmlFor='contract-operation-log-type'>Operación</label>
            <Dropdown
              inputId='contract-operation-log-type'
              value={operationType}
              options={options.operationTypes}
              optionLabel='label'
              optionValue='code'
              placeholder='Todas'
              showClear
              onChange={(e) => setOperationType(e.value ?? null)}
            />
          </div>
          <div className='nv-field contract-operation-log__status'>
            <label htmlFor='contract-operation-log-status'>Estado</label>
            <Dropdown
              inputId='contract-operation-log-status'
              value={status}
              options={options.statuses}
              optionLabel='label'
              optionValue='code'
              placeholder='Todos'
              showClear
              onChange={(e) => setStatus(e.value ?? null)}
            />
          </div>
          <div className='nv-field contract-operation-log__health-entity'>
            <label htmlFor='contract-operation-log-health-entity'>Obra social</label>
            <Dropdown
              inputId='contract-operation-log-health-entity'
              value={healthEntityId}
              options={options.healthEntities}
              optionLabel='name'
              optionValue='id'
              placeholder='Todas'
              filter
              showClear
              emptyFilterMessage='No hay obras sociales con ese nombre'
              panelClassName='contract-operation-log__health-entity-panel'
              onChange={(e) => setHealthEntityId(e.value ?? null)}
            />
          </div>
          <div className='nv-row-8 contract-operation-log__filter-actions'>
            <Button type='button' className='buttonMain3' label='Consultar' onClick={handleConsult} />
            <Button
              type='button'
              rounded
              className='nv-control nv-tip'
              icon='pi pi-eraser'
              aria-label='Limpiar filtros'
              data-pr-tooltip='Limpiar filtros'
              onClick={clearFilters}
            />
          </div>
        </div>
      </section>

      <div className='contract-operation-log__table'>
        <RemoteTable
          idTable='contractOperationLog'
          dataKey='id'
          getItems={loadPage}
          params={appliedFilter}
          paramsDelay={0}
          filterRow={false}
          restoreScroll={backFromDetail}
          snapshotKey={JSON.stringify(appliedFilter)}
          rowActions={rowActions}
          tableMinWidth={1200}
          notFoundText={Object.values(appliedFilter).some(value => value !== undefined)
            ? 'No hay resultados para estos filtros.'
            : 'Todavía no hay operaciones registradas.'}
        >
          <RColumn field='startedAt' header='Fecha y hora' className='contract-operation-log__nowrap'
            style={STARTED_AT_STYLE} body={startedAtBody} />
          <RColumn field='userName' header='Usuario' style={AUTO_STYLE} body={userBody} />
          <RColumn field='operationTypeLabel' header='Operación' style={AUTO_STYLE} />
          <RColumn field='statusLabel' header='Estado' style={STATUS_STYLE} />
          <RColumn field='qtyTotal' header='Contratos' style={COUNTS_STYLE} body={countsBody} />
          <RColumn field='description' header='Parámetros' style={DESCRIPTION_STYLE} body={descriptionBody} />
        </RemoteTable>
      </div>
    </div>
  )
}
